import { getAbilityData } from '../../../shared/content/AbilityDataAdapter';
import { ABILITY_UNLOCK_LEVELS } from '../../../shared/content/generated/hotbars.gen';
import type { IconSchool } from './iconPalette';
import type { Motif, MotifVariant } from './motifs';

/** Remaining canonical trainer unlocks, composed from existing authored glyph families. */
const CURVE_MOTIFS: ReadonlyArray<readonly [id: string, motif: Motif, variant?: MotifVariant]> = [
  ['ground_slam', 'nova', 'ground'],
  ['heroic_leap', 'dash'],
  ['avatar', 'shield', 'ironwake'],
  ['shield_wall', 'shield'],
  ['arcane_missiles', 'orb', 'spike'],
  ['arcane_barrage', 'nova'],
  ['ice_barrier', 'shield'],
  ['combustion', 'orb', 'ember'],
  ['chain_lightning', 'bolt', 'strike'],
  ['divine_shield', 'shield'],
  ['flash_of_light', 'heal'],
  ['avenging_wrath', 'nova', 'sunvow'],
  ['wake_of_ashes', 'nova', 'ground'],
  ['prayer_of_mending', 'heal', 'hot'],
  ['divine_hymn', 'heal', 'greater'],
  ['shadow_word_pain', 'curse', 'agony'],
  ['fade', 'dash', 'back'],
  ['barrage', 'multiArrow', 'rain'],
  ['rapid_fire', 'multiArrow'],
  ['kill_shot', 'arrow', 'aimed'],
  ['deterrence', 'shield'],
  ['vanish', 'dash', 'back'],
  ['cloak_of_shadows', 'shield'],
  ['shadow_dance', 'whirl', 'knives'],
  ['killing_spree', 'dagger', 'fatal'],
  ['windfury_totem', 'totem', 'galeheart'],
  ['chain_heal', 'heal', 'leafBig'],
  ['ghost_wolf', 'dash'],
  ['bloodlust', 'blade', 'heavy'],
  ['life_tap', 'orb', 'bloodwell'],
  ['curse_of_elements', 'curse', 'veilbreak'],
  ['howl_of_terror', 'nova'],
  ['demon_armor', 'shield'],
  ['tranquility', 'heal', 'leafBig'],
  ['mark_of_the_wild', 'roots'],
  ['barkskin', 'shield'],
  ['hurricane', 'whirl'],
];

export interface CurveAbilityIconSpec { id: string; motif: Motif; school: IconSchool; variant?: MotifVariant }

/** Tint uses canonical ability school; only glyph choice belongs to this art layer. */
export const CURVE_ABILITY_ICON_SPECS: readonly CurveAbilityIconSpec[] = Object.freeze(CURVE_MOTIFS.map(([id, motif, variant]) => {
  const school = getAbilityData(id)?.school;
  // Each admitted curve ID has an authored school; fail closed on catalog drift.
  if (!school || school === 'chaos') throw new Error(`Unsupported canonical icon school for curve ability: ${id}`);
  return Object.freeze({ id, motif, school, ...(variant ? { variant } : {}) });
}));

/** Separate full-curve contact-sheet groups; original starter-sheet grouping stays stable. */
export const CURVE_ICON_CLASS_GROUPS: Readonly<Record<string, readonly string[]>> = Object.freeze(Object.fromEntries(
  Object.entries(ABILITY_UNLOCK_LEVELS).filter(([classId]) => classId !== 'dev')
    .map(([classId, curve]) => [classId, Object.freeze(curve.map(entry => entry.abilityId))]),
));
